import { DownPayment, DownPaymentRequest, DownPaymentSummary, FinalInvoiceRequest } from '../types/downPaymentTypes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://invoice-service-php-685396932615.europe-west3.run.app';

export interface DownPaymentApiResponse {
  success: boolean;
  data?: any;
  error?: string;
}

/**
 * Erstellt eine neue Abschlagsrechnung
 */
export const createDownPayment = async (
  request: DownPaymentRequest,
  token: string
): Promise<{ blob: Blob; filename: string }> => {
  // NEU: Dokumentsprache im Header für Backend-Logging
  const headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  
  if (request.documentLanguage) {
    headers['X-Document-Language'] = request.documentLanguage;
  }
  
  const response = await fetch(`${API_BASE_URL}/api/down-payment/create`, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  // Verarbeite die Response basierend auf Content-Type
  const contentType = response.headers.get('content-type') || '';
  
  if (contentType.includes('application/pdf')) {
    // PDF-Response verarbeiten (ZUGFeRD)
    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'Abschlagsrechnung.pdf';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    return { blob, filename };
  } else if (contentType.includes('application/xml')) {
    // XML-Response verarbeiten (XRechnung)
    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'Abschlagsrechnung.xml';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    return { blob, filename };
  } else {
    // Fallback für andere Content-Types (z.B. JSON während Entwicklung)
    const text = await response.text();
    const blob = new Blob([text], { type: 'text/plain' });
    const filename = 'Abschlagsrechnung.txt';
    
    return { blob, filename };
  }
};

/**
 * Erstellt eine Endrechnung mit Abzug aller Abschlagsrechnungen
 */
export const createFinalInvoice = async (
  request: FinalInvoiceRequest,
  token: string
): Promise<{ blob: Blob; filename: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/down-payment/final-invoice`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  // PDF-Response verarbeiten
  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition');
  let filename = 'Endrechnung.pdf';
  
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  return { blob, filename };
};

/**
 * Lädt alle Abschlagsrechnungen für ein bestimmtes Basis-Dokument
 */
export const getDownPayments = async (
  baseDocumentId: string,
  token: string
): Promise<DownPaymentSummary> => {
  const response = await fetch(`${API_BASE_URL}/api/down-payment/list?baseDocumentId=${encodeURIComponent(baseDocumentId)}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return await response.json();
};

/**
 * Berechnet den Abschlagsbetrag basierend auf Typ und Wert
 */
export const calculateDownPaymentAmount = (
  totalAmount: number,
  downPaymentType: 'amount' | 'percentage',
  downPaymentValue: number
): number => {
  if (downPaymentType === 'amount') {
    return Math.round(downPaymentValue * 100) / 100; // Auf 2 Dezimalstellen runden
  } else {
    return Math.round((totalAmount * downPaymentValue / 100) * 100) / 100;
  }
};

/**
 * Validiert die Abschlagsrechnung-Eingaben
 */
export const validateDownPaymentInput = (
  totalAmount: number,
  existingDownPayments: DownPayment[],
  downPaymentType: 'amount' | 'percentage',
  downPaymentValue: number
): { isValid: boolean; error?: string } => {
  // Basis-Validierungen
  if (downPaymentValue <= 0) {
    return { isValid: false, error: 'Der Abschlagsbetrag muss größer als 0 sein' };
  }

  if (downPaymentType === 'percentage' && downPaymentValue >= 100) {
    return { isValid: false, error: 'Der Prozentsatz muss kleiner als 100% sein' };
  }

  // Berechne neuen Abschlagsbetrag
  const newDownPaymentAmount = calculateDownPaymentAmount(totalAmount, downPaymentType, downPaymentValue);
  
  // Berechne bereits vorhandene Abschläge
  const totalExistingDownPayments = existingDownPayments.reduce((sum, dp) => sum + dp.amount, 0);
  
  // Prüfe ob die Summe den Gesamtbetrag überschreiten würde
  if ((totalExistingDownPayments + newDownPaymentAmount) > totalAmount) {
    const maxAllowed = totalAmount - totalExistingDownPayments;
    return { 
      isValid: false, 
      error: `Abschlagssumme würde den Gesamtbetrag überschreiten. Maximal noch ${maxAllowed.toFixed(2)} € möglich.` 
    };
  }

  return { isValid: true };
};

/**
 * Formatiert einen Geldbetrag für die Anzeige
 */
export const formatCurrency = (amount: number, currency: string = 'EUR'): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

/**
 * Hilfsfunktion zum Download von PDF-Blobs
 */
export const downloadPdfBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Berechnet die Abschlagsstatistiken für ein Basis-Dokument
 */
export const calculateDownPaymentStatistics = (
  totalAmount: number,
  downPayments: DownPayment[]
): {
  totalDownPayments: number;
  remainingAmount: number;
  completionPercentage: number;
  canCreateFinalInvoice: boolean;
} => {
  const totalDownPayments = downPayments.reduce((sum, dp) => sum + dp.amount, 0);
  const remainingAmount = Math.max(0, totalAmount - totalDownPayments);
  const completionPercentage = Math.round((totalDownPayments / totalAmount) * 100);
  const canCreateFinalInvoice = downPayments.length > 0 && remainingAmount > 0;

  return {
    totalDownPayments,
    remainingAmount,
    completionPercentage,
    canCreateFinalInvoice
  };
};

/**
 * Lädt alle existierenden Abschlagsrechnungen für einen Workflow
 */
export const getExistingDownPayments = async (
  workflowReferenceId: string,
  token: string
): Promise<DownPayment[]> => {
  console.log('[getExistingDownPayments] ========== STARTING API CALL ==========');
  console.log('[getExistingDownPayments] workflowReferenceId:', workflowReferenceId);
  console.log('[getExistingDownPayments] token length:', token.length);
  console.log('[getExistingDownPayments] API_BASE_URL:', API_BASE_URL);
  
  const url = `${API_BASE_URL}/api/down-payment/list?workflowReferenceId=${encodeURIComponent(workflowReferenceId)}`;
  console.log('[getExistingDownPayments] Full URL:', url);
  
  console.log('[getExistingDownPayments] Making fetch request...');
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  console.log('[getExistingDownPayments] Response status:', response.status);
  console.log('[getExistingDownPayments] Response ok:', response.ok);
  console.log('[getExistingDownPayments] Response headers:', Object.fromEntries(response.headers.entries()));

  if (!response.ok) {
    console.error('[getExistingDownPayments] Response not OK, getting error data...');
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    console.error('[getExistingDownPayments] Error data:', errorData);
    throw new Error(errorData.error || `Failed to load down payments: ${response.status}`);
  }

  const result = await response.json();
  console.log('[getExistingDownPayments] ✅ Response data:', result);
  console.log('[getExistingDownPayments] ✅ Result type:', typeof result);
  console.log('[getExistingDownPayments] ✅ Result is array:', Array.isArray(result));
  console.log('[getExistingDownPayments] ✅ Result length:', Array.isArray(result) ? result.length : 'not array');
  
  return result;
};

/**
 * Lädt alle Abschlagsrechnungen für eine Workflow-Referenz-ID (vereinfachte Version)
 */
export const getDownPaymentsByWorkflowId = async (
  workflowReferenceId: string,
  token: string
): Promise<DownPayment[]> => {
  const response = await fetch(`${API_BASE_URL}/api/down-payment/list?workflowReferenceId=${encodeURIComponent(workflowReferenceId)}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

/**
 * Erstellt eine Abschlussrechnung (automatischer Restbetrag)
 */
export const createFinalInvoiceFromDownPayments = async (
  request: FinalInvoiceRequest,
  token: string
): Promise<{ blob: Blob; filename: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/down-payment/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      ...request,
      finalInvoiceType: 'final', // Kennzeichnet dies als Abschlussrechnung
      downPaymentType: 'amount', // Automatisch berechnet
      downPaymentValue: request.remainingAmount || 0 // Restbetrag als Wert
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  // Verarbeite die Response basierend auf Content-Type
  const contentType = response.headers.get('content-type') || '';
  
  if (contentType.includes('application/pdf')) {
    // PDF-Response verarbeiten (ZUGFeRD)
    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'Abschlussrechnung.pdf';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    return { blob, filename };
  } else if (contentType.includes('application/xml')) {
    // XML-Response verarbeiten (XRechnung)
    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'Abschlussrechnung.xml';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    return { blob, filename };
  } else {
    // Fallback für andere Content-Types
    const text = await response.text();
    const blob = new Blob([text], { type: 'text/plain' });
    const filename = 'Abschlussrechnung.txt';
    
    return { blob, filename };
  }
}; 